/**
 * What a promotion's add-all button sends, as pure functions: the transformers
 * `useAddProductFromPromotion` runs, kept out of the hooks barrel so the stock
 * check can ask the same question without it becoming public API.
 */
import {
  PromotionType,
  ComplexPromotionTypes,
  IPackage,
} from '../../graphQL/commons'
import { getPromotionFields } from '../common/getPromotionFields'
import type { IAddPrefillCartItem } from '../../hooks/prefillCart/useAddPrefillCart'
import {
  ICommonPromotionFields,
  IConditionAction,
  IProduct,
  IPromotionShortcutData,
} from '../../interfaces'

interface ITransformerItemsArgs {
  currentItems: IAddPrefillCartItem[]
  autoAdd: boolean
  promotionsFields: ICommonPromotionFields
  selectedProduct?: IProduct
  groupIdentifier?: string
}

/**
 * Merges cart lines that share the same `sku` and `package.name`,
 * summing their quantities. Keys on the tuple `[sku, packageName]`
 * serialized with `JSON.stringify`, so a SKU containing `+` is not
 * truncated and `{ sku: 'A+B', package: null }` cannot collide with
 * `{ sku: 'A', package: { name: 'B' } }`. `null` discriminates "no
 * package" from "package whose `name` is `''`".
 *
 * The first line seen for a key is carried as the base; a later line's
 * quantity is added. When the base's `name` is `''` or `price` is `0`
 * and a later merged line carries a real value, the later value wins —
 * the first-seen branch for a SKU may be one that sets `name: ''` /
 * `price: 0` while a later branch populates them from the shopper's
 * `selectedProduct`. Exported so it can be exercised directly in tests.
 */
export const dedupPromotionItems = (
  items: IAddPrefillCartItem[],
): IAddPrefillCartItem[] => {
  const merged = new Map<string, IAddPrefillCartItem>()
  items.forEach((item) => {
    const key = JSON.stringify([item.sku, item.package?.name ?? null])
    const prior = merged.get(key)
    if (!prior) {
      merged.set(key, item)
      return
    }
    merged.set(key, {
      ...prior,
      quantity: prior.quantity + item.quantity,
      name: prior.name || item.name,
      price: prior.price || item.price,
    })
  })
  return Array.from(merged.values())
}

export const EmptyPackage: IPackage = {
  name: '',
  label: '',
  quantity: 0,
  price: 0,
  jumpQty: 0,
  minQty: null,
  maxQty: null,
  stock: null,
  outOfStock: null,
}
/**
 * Array of conditional transformer objects for building the list of cart items
 * to add from a promotion, depending on the promotion type, selection state, and group alignment.
 * Each entry contains a `condition` to match the current context, and a `transformAction`
 * to produce an updated list of cart items.
 *
 * The array is composed of IConditionAction<ITransformerItemsArgs, IAddPrefillCartItem[]> objects.
 */
const transformersItems: IConditionAction<
  ITransformerItemsArgs,
  IAddPrefillCartItem[]
>[] = [
  /**
   * Handles COMBO promotions where a specific product is selected, and there is no package specified.
   * Adds the selected product's SKU to the cart, using the defined buy quantity, with no package info.
   */
  {
    condition: ({ promotionsFields: { type, customerBuysPackage }, selectedProduct }) =>
      type === PromotionType.COMBO && !!selectedProduct && !customerBuysPackage,
    transformAction: ({
      selectedProduct,
      promotionsFields: { customerBuysQuantity: quantity },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku: selectedProduct!.sku,
        quantity,
        package: null,
        price: selectedProduct?.price || 0,
        name: selectedProduct?.name || '',
      },
    ],
  },
  /**
   * Handles COMBO promotions where a specific product and a buy package are selected.
   * Adds the selected product's SKU and the specified package to the cart, using buy quantity.
   */
  {
    condition: ({ promotionsFields: { type, customerBuysPackage }, selectedProduct }) =>
      type === PromotionType.COMBO && !!selectedProduct && !!customerBuysPackage,
    transformAction: ({
      selectedProduct,
      promotionsFields: { customerBuysQuantity: quantity, customerBuysPackage },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku: selectedProduct!.sku,
        quantity,
        package: {
          ...EmptyPackage,
          name: customerBuysPackage!,
        },
        price: selectedProduct?.price || 0,
        name: selectedProduct?.name || '',
      },
    ],
  },
  /**
   * Handles COMBO promotions (autoAdd OFF) for "customer gets" combo options.
   * Adds the first `customerGetsItems` SKU and its defined quantity, no package, to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerGetsItems, customerGetsQuantity, customerGetsPackage },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !autoAdd &&
      !!customerGetsItems.length &&
      !!customerGetsQuantity && !customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsQuantity: quantity,
        customerGetsItems: [sku],
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity,
        package: null,
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions (autoAdd OFF) when a get package is specified.
   * Adds the first gets SKU and its quantity, with the specified package, to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerGetsItems, customerGetsQuantity, customerGetsPackage },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !autoAdd &&
      !!customerGetsItems.length &&
      !!customerGetsQuantity &&
      !!customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsQuantity: quantity,
        customerGetsItems: [sku],
        customerGetsPackage,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity,
        package: {
          ...EmptyPackage,
          name: customerGetsPackage!,
        },
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions with autoAdd enabled and a percentage discount active.
   * Adds the first gets SKU and its quantity (no package) to the cart automatically.
   */
  {
    condition: ({
      promotionsFields: {
        type,
        customerGetsItems,
        customerGetsQuantity,
        customerGetsPercentage,
        customerGetsPackage,
      },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !!autoAdd &&
      !!customerGetsQuantity &&
      !!customerGetsItems.length &&
      !!customerGetsPercentage &&
      !customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsItems: [sku],
        customerGetsQuantity,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity: customerGetsQuantity,
        package: null,
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions with autoAdd enabled, a percentage discount, and a gets package.
   * Adds the first gets SKU and its quantity, and also sets the gets package.
   */
  {
    condition: ({
      promotionsFields: {
        type,
        customerGetsItems,
        customerGetsQuantity,
        customerGetsPercentage,
        customerGetsPackage,
      },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !!autoAdd &&
      !!customerGetsQuantity &&
      !!customerGetsItems.length &&
      !!customerGetsPercentage &&
      !!customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsItems: [sku],
        customerGetsQuantity,
        customerGetsPackage,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity: customerGetsQuantity,
        package: {
          ...EmptyPackage,
          name: customerGetsPackage!,
        },
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMPLEX promotions where there is exactly one buys group and one gets group.
   * Adds all SKUs from the only buys group to the cart, ignoring missing SKUs.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length === 1 &&
      customerGetsGroups.length === 1,
    transformAction: ({
      promotionsFields: { customerBuysGroups },
      currentItems,
    }) => {
      return [
        ...currentItems,
        // Only support one customerBuys group and one customerGets group
        ...customerBuysGroups[0].items
          .filter(({ sku }) => sku !== null)
          .map(({ sku, quantity }) => ({
            sku,
            quantity,
            package: null,
            price: 0,
            name: '',
          })),
      ]
    },
  },
  /**
   * Handles COMPLEX promotions with multiple buys groups, but only one gets group.
   * Appends all SKUs from the target buys group (matching identifier) to the cart, skipping null SKUs.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length > 1 &&
      customerGetsGroups.length === 1,
    transformAction: ({
      promotionsFields: { customerBuysGroups },
      currentItems,
      groupIdentifier,
    }) => {
      const group = customerBuysGroups.find(
        (group) => group.identifier === groupIdentifier,
      )
      if (group) {
        return [
          ...currentItems,
          ...group.items
            .filter(({ sku }) => sku !== null)
            .map(({ sku, quantity }) => ({
              sku,
              quantity,
              package: null,
              price: 0,
              name: '',
            })),
        ]
      }
      return currentItems
    },
  },
  /**
   * Handles COMPLEX promotions (with exactly one buys group and one gets group),
   * when the gets group is either:
   * (A) Type is not DIRECT_PERCENTAGE and autoAdd is disabled, or
   * (B) Type is PERCENTAGE and autoAdd is enabled.
   * Adds all SKUs from the gets group to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
      autoAdd,
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length === 1 &&
      customerGetsGroups.length === 1 &&
      ((customerGetsGroups[0].type !==
        ComplexPromotionTypes.DIRECT_PERCENTAGE &&
        !autoAdd) ||
        (customerGetsGroups[0].type === ComplexPromotionTypes.PERCENTAGE &&
          !!autoAdd)),
    transformAction: ({
      promotionsFields: { customerGetsGroups },
      currentItems,
    }) => {
      return [
        ...currentItems,
        // Only support one customerBuys group and one customerGets group
        ...customerGetsGroups[0].items
          .filter(
            (item): item is typeof item & { sku: string; quantity: number } =>
              item.sku !== null,
          )
          .map(({ sku, quantity }) => ({
            sku,
            quantity,
            package: null,
            name: '',
            price: 0,
          })),
      ]
    },
  },
  /**
   * Deduplication step. Delegates to `dedupPromotionItems` above —
   * see that function's docstring for the merge contract.
   */
  {
    condition: ({ currentItems: { length } }) => length > 1,
    transformAction: ({ currentItems }) => dedupPromotionItems(currentItems),
  },
]

interface IGetPromotionItemsToAddArgs extends IPromotionShortcutData {
  autoAdd: boolean
  selectedProduct?: IProduct
}

/**
 * The cart lines the add-all button sends for a promotion — the pure half of
 * `addProductFromPromotion`, exported so the stock check can ask what a
 * button would add rather than re-deriving it.
 */
export const getPromotionItemsToAdd = ({
  promotion,
  groupIdentifier,
  autoAdd,
  selectedProduct,
}: IGetPromotionItemsToAddArgs): IAddPrefillCartItem[] => {
  if (!promotion) return []
  const transformItemsArgs: ITransformerItemsArgs = {
    currentItems: [],
    autoAdd,
    promotionsFields: getPromotionFields(promotion),
    selectedProduct,
  }

  return transformersItems.reduce<IAddPrefillCartItem[]>(
    (acc, { condition, transformAction }) =>
      condition({
        ...transformItemsArgs,
        currentItems: acc,
        groupIdentifier: groupIdentifier || '',
      })
        ? transformAction({
            ...transformItemsArgs,
            currentItems: acc,
            groupIdentifier: groupIdentifier || '',
          })
        : acc,
    [],
  )
}
